/**
 * Haptic engine — Capacitor.Haptics natif iOS/Android + web fallback graceful.
 *
 * Caractéristiques :
 *   - iOS/Android natif : `Capacitor.isNativePlatform() === true`
 *     → `Haptics.impact({ style })` ou `Haptics.notification({ type })`.
 *   - Web fallback : `navigator.vibrate(pattern)` (Safari iOS limited, Android Chrome OK).
 *   - Test env (jsdom + mocked) : Capacitor.isNativePlatform mocké à false par défaut,
 *     navigator.vibrate stub → assertion sur les patterns appelés.
 *
 * Mapping intensity → pattern web :
 *   light  → 10ms
 *   medium → 20ms
 *   heavy  → [40, 30, 20]
 */
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
export function createHapticEngine() {
    return {
        async impact(intensity) {
            if (isNative()) {
                try {
                    await Haptics.impact({ style: toImpactStyle(intensity) });
                }
                catch {
                    /* native plugin missing or device sans haptic — silent fallback */
                }
                return;
            }
            vibrateWeb(intensityToWebPattern(intensity));
        },
        async pattern(p) {
            if (isNative()) {
                // Sur natif on simule le pattern via vibrate fallback Web (Capacitor.Haptics
                // n'a pas d'API pattern arbitraire). Acceptable car pattern n'est utilisé
                // que côté web fallback.
                vibrateWeb(p);
                return;
            }
            vibrateWeb(p);
        },
        async notification(type) {
            if (isNative()) {
                try {
                    await Haptics.notification({ type: toNotificationType(type) });
                }
                catch {
                    /* ignored */
                }
                return;
            }
            vibrateWeb(notificationToWebPattern(type));
        },
    };
}
function isNative() {
    try {
        return Capacitor.isNativePlatform();
    }
    catch {
        return false;
    }
}
function vibrateWeb(pattern) {
    if (typeof navigator === 'undefined' || !('vibrate' in navigator))
        return;
    try {
        navigator.vibrate(pattern);
    }
    catch {
        /* navigator.vibrate refuse parfois (privacy / older browsers) */
    }
}
function toImpactStyle(intensity) {
    switch (intensity) {
        case 'light':
            return ImpactStyle.Light;
        case 'medium':
            return ImpactStyle.Medium;
        case 'heavy':
            return ImpactStyle.Heavy;
    }
}
function toNotificationType(type) {
    switch (type) {
        case 'success':
            return NotificationType.Success;
        case 'warning':
            return NotificationType.Warning;
        case 'error':
            return NotificationType.Error;
    }
}
function intensityToWebPattern(intensity) {
    switch (intensity) {
        case 'light':
            return 10;
        case 'medium':
            return 20;
        case 'heavy':
            return [40, 30, 20];
    }
}
function notificationToWebPattern(type) {
    switch (type) {
        case 'success':
            return 12;
        case 'warning':
            return [10, 40, 10];
        case 'error':
            return [40, 60, 40];
    }
}
//# sourceMappingURL=haptic-engine.js.map